import { Empty } from 'antd';
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ErrorBoundary as BugSnagErrorBoundary } from 'utils/bugsnag';

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 16px;
`;

/**
 * Compact, tile-sized error fallback. The app's shared ErrorBoundary renders a
 * full-page "system problems" screen (turbine image + footer), which looks broken
 * inside a single dashboard cell — so each widget gets this contained fallback
 * instead. Errors are still reported via the same Bugsnag boundary.
 */
function WidgetErrorFallback() {
  const { t } = useTranslation();
  return (
    <Centered>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('dashboard.widgets.loadError', 'Couldn’t load this widget')}
      />
    </Centered>
  );
}

type WidgetErrorBoundaryProps = {
  children: ReactElement;
};

export function WidgetErrorBoundary({ children }: WidgetErrorBoundaryProps) {
  return (
    <BugSnagErrorBoundary FallbackComponent={WidgetErrorFallback}>{children}</BugSnagErrorBoundary>
  );
}
